import {
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  alpha,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

// Adiciona o plugin de trimestre ao dayjs
dayjs.extend(quarterOfYear);

export type MonthQuarterFilterType = "month" | "quarter";

interface MonthQuarterFilterProps {
  selectedDate: dayjs.Dayjs;
  dateFilterType: MonthQuarterFilterType;
  onDateChange: (date: dayjs.Dayjs) => void;
  onFilterTypeChange: (type: MonthQuarterFilterType) => void;
}

const DATE_FILTER_OPTIONS: { value: MonthQuarterFilterType; label: string }[] =
  [
    { value: "month", label: "Mês" },
    { value: "quarter", label: "Trimestre" },
  ];

/**
 * Variante do DateRangeFilter que permite apenas seleção de mês ou trimestre
 */
export const MonthQuarterFilter = ({
  selectedDate,
  dateFilterType,
  onDateChange,
  onFilterTypeChange,
}: MonthQuarterFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePreviousDate = () => {
    onDateChange(
      dateFilterType === "quarter"
        ? selectedDate.subtract(1, "quarter")
        : selectedDate.subtract(1, "month")
    );
  };

  const handleNextDate = () => {
    onDateChange(
      dateFilterType === "quarter"
        ? selectedDate.add(1, "quarter")
        : selectedDate.add(1, "month")
    );
  };

  const handleOpenDateFilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDateFilter = () => {
    setAnchorEl(null);
  };

  const handleDateFilterChange = (type: MonthQuarterFilterType) => {
    onFilterTypeChange(type);
    handleCloseDateFilter();
  };

  const getDateRangeText = () => {
    if (dateFilterType === "quarter") {
      const startOfQuarter = selectedDate.startOf("quarter");
      const endOfQuarter = selectedDate.endOf("quarter");
      const quarterNumber = selectedDate.quarter();

      return `${quarterNumber}º Trimestre de ${selectedDate.format(
        "YYYY"
      )} (${startOfQuarter.format("MMM")} - ${endOfQuarter.format("MMM")})`;
    }

    return selectedDate.format("MMMM [de] YYYY");
  };

  const open = Boolean(anchorEl);

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Tooltip title="Clique para alterar o tipo de visualização" arrow>
        <Button
          onClick={handleOpenDateFilter}
          variant="outlined"
          endIcon={<KeyboardArrowDown />}
          sx={{
            textTransform: "none",
            minWidth: "120px",
            borderStyle: "dashed",
            "&:hover": {
              borderStyle: "solid",
            },
          }}
        >
          {
            DATE_FILTER_OPTIONS.find((opt) => opt.value === dateFilterType)
              ?.label
          }
        </Button>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseDateFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <List sx={{ width: 200, py: 0 }}>
          {DATE_FILTER_OPTIONS.map((option) => (
            <ListItemButton
              key={option.value}
              selected={dateFilterType === option.value}
              onClick={() => handleDateFilterChange(option.value)}
            >
              <ListItemText
                primary={option.label}
                secondary={
                  option.value === dateFilterType ? "Selecionado" : null
                }
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>

      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="Período anterior" arrow>
          <IconButton
            onClick={handlePreviousDate}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <ChevronLeft />
          </IconButton>
        </Tooltip>

        <Typography
          variant="body1"
          sx={{
            minWidth: 300,
            textAlign: "center",
            color: "text.primary",
            fontWeight: "medium",
          }}
        >
          {getDateRangeText()}
        </Typography>

        <Tooltip title="Próximo período" arrow>
          <IconButton
            onClick={handleNextDate}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
